<template>
  <div id="call_engagement_activity" class="flex flex-col gap-2">
    <ContainerInfoItem
      :source="`call_engagement_activity_${type}`"
      :title="getTitle"
      :description="range.label"
      height="fit-content"
    >
      <SkeletonLoader
        v-if="isLoading"
        :page="
          type === analyticTypes.TABLE ? loaderPages.TABLE : loaderPages.CHART
        "
      />

      <template v-else>
        <CustomTable
          v-if="type === analyticTypes.TABLE && allTableData?.length > 0"
          :headers="callEngagementTableHeaders"
          :data="paginatedData"
          :show-page-size="false"
          :pagination="pagination"
          @update:pagination="handlePageChange"
          class="mt-4"
        />

        <LineChart
          v-else-if="
            type === analyticTypes.CHART && chartCategories && chartData
          "
          id="call_engagement_activity"
          :categories="chartCategories"
          :series-data="chartData"
        />

        <EmptyScreen
          v-else
          :source="`call_engagement_activity_${type}`"
          text="No data found!"
          :type="emptyScreenTypes.CHART_AND_TABLE"
        />
      </template>
    </ContainerInfoItem>
  </div>
</template>

<script setup>
import { computed, ref, watchEffect } from "vue";
import { renderEntity } from "@/src/utils/utils.js";
import { processLineChartData } from "@/src/utils/chartUtils.js";
import {
  analyticTypes,
  callEngagementTableHeaders,
} from "@/src/constants/analyticConstants.js";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import LineChart from "@/src/components/charts/LineChart.vue";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: (value) =>
      [analyticTypes.TABLE, analyticTypes.CHART].includes(value),
  },
});

const filters = computed(() => store.getters["analytics/getFilters"]);
const range = computed(() => store.getters["analytics/getRange"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const isLoading = ref(false);
const allTableData = ref([]);
const chartCategories = ref(null);
const chartData = ref(null);
const pagination = ref({
  current: 1,
  pageSize: 5,
  total: 0,
});

const paginatedData = computed(() => {
  if (!allTableData.value) return [];

  const start = (pagination.value.current - 1) * pagination.value.pageSize;
  const end = start + pagination.value.pageSize;

  return allTableData.value.slice(start, end);
});

const getTitle = computed(() => {
  if (props.type === analyticTypes.TABLE) {
    return `Call engagement activity by ${renderEntity(filters.value?.user) || "user"}`;
  }
  return "Call engagement activity";
});

const resetData = () => {
  chartCategories.value = null;
  chartData.value = null;
  allTableData.value = [];
  pagination.value = {
    current: 1,
    pageSize: 5,
    total: 0,
  };
};

const handlePageChange = (newPagination) => {
  pagination.value = { ...pagination.value, current: newPagination.current };
};

const processChartData = (data) => {
  if (!data?.length) {
    resetData();
    return;
  }

  const { categories, seriesData } = processLineChartData(data, {
    dateKey: "date",
    metrics: [
      { key: "calls_connected", label: "Calls Connected" },
      { key: "calls_dialed", label: "Calls Dialed" },
    ],
  });

  chartCategories.value = categories;
  chartData.value = seriesData;

  if (
    !chartData.value.some((series) => series.data.some((value) => value !== 0))
  ) {
    resetData();
  }
};

const getParams = () => ({
  user_id: userIds.value,
  team_id: teamIds.value,
  start_time: range.value?.start,
  end_time: range.value?.end,
});

const fetchData = async () => {
  try {
    isLoading.value = true;
    const response =
      props.type === analyticTypes.TABLE
        ? await AnalyticService.V2.fetchCallEngagementByUser(getParams())
        : await AnalyticService.V2.fetchCallEngagement(getParams());

    if (response?.success && response?.data) {
      if (props.type === analyticTypes.TABLE) {
        const { data, total_output } = response.data.data;
        allTableData.value = data || [];
        pagination.value.total = total_output;
      } else {
        processChartData(response.data);
      }
    } else {
      resetData();
    }
  } catch (error) {
    console.error("Error fetching call engagement:", error);
    resetData();
  } finally {
    isLoading.value = false;
  }
};

watchEffect(() => {
  if (range.value && userIds.value && teamIds.value) {
    resetData();
    fetchData();
  }
});

useAnalyticsRefresh(fetchData, [range, userIds, teamIds]);
</script>
